import { ChangeDetectorRef, Component, ViewChild, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription, interval } from 'rxjs';
import { PortWeatherService } from 'src/app/shared/service/port-weather/port-weather.service';
import { ToasterMessages } from 'src/app/shared/constants/constants';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { LoginService } from 'src/app/shared/service/login/login.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
import { formatPcUtcDateTime } from 'src/app/shared/helpers/pc-utc-datetime.util';

@Component({
  selector: 'app-pw-my-requests',
  templateUrl: './pw-my-requests.component.html',
  styleUrls: ['./pw-my-requests.component.scss']
})
export class PwMyRequestsComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns: string[] = [
    'sno',
    'vessel Name',
    'port Name',
    'date',
    'Frequency',
    'Number of Days',
    'file Name',
    'status',
  ];
  
  // Initialize with dummy items so mat-table renders the skeleton rows
  dataSource = new MatTableDataSource<any>([1, 2, 3, 4, 5] as any);
  isLoadingTable: boolean = true; 

  private refreshInterval!: Subscription;
  isStatusClickable: boolean = false;
  filterForm!: FormGroup;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private portWeather: PortWeatherService,
    public dialog: MatDialog,
    private loginService: LoginService,
    private requestFormService: RequestFormService
  ) {
    this.filterForm = this.fb.group({
      from_date: ['', [Validators.required]],
      to_date: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    this.loadData();

    this.refreshInterval = interval(30000).subscribe(() => {
      this.loadData(false);
      this.cdr.detectChanges();
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy() {
    if (this.refreshInterval) {
      this.refreshInterval.unsubscribe();
    }
  }

  loadData(showLoading = true) {
    if (showLoading) {
      this.isLoadingTable = true;
    }

    this.portWeather.getTableData().subscribe({
      next: (data) => {
        if (data && data.data) {
          this.dataSource.data = data.data.map((res: any, index: number) => ({
            sno: index + 1,
            'vessel Name': res.vessel_name,
            'port Name': res.port_name,
            date: formatPcUtcDateTime(res.report_date),
            Frequency: res.frequency,
            'Number of Days': res.days,
            'file Name': this.extractShortNameFromUrl(res.pdf_link),
            status: res.status ? res.status.toLowerCase() : 'pending',
            originalFileName: this.extractFilenameOnly(res.pdf_link),
          }));
          this.cdr.detectChanges();
        }
        this.isLoadingTable = false;
      },
      error: (error) => {
        this.toastr.error(ToasterMessages.Error);
        this.isLoadingTable = false;
      }
    });
  }

  extractFilenameOnly(url: string): string {
    if (!url) return '';
    const regex = /pdffilename=(.*?)(?:&|$)/i;
    const match = url.match(regex);
    if (match && match[1]) {
      return match[1];
    }
    return url.split('/').pop() || '';
  }

  extractShortNameFromUrl(url: string): string {
    if (!url) return 'Report.pdf';
    const filename = this.extractFilenameOnly(url);
    return filename || 'Report.pdf';
  }

  getStatusBadgeClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'processing' || s === 'pending') return 'status-badge processing';
    if (s === 'failed' || s === 'error') return 'status-badge failed';
    return 'status-badge';
  }

  onBackClick() {
    this.router.navigate(['/pw-home']);
  }

  onStatusClick(element: any) {
    const status = (element.status || '').toLowerCase();
    if (status === 'ready' || status === 'completed' || status === 'ready to download') {
      const filename = element.originalFileName;

      this.portWeather.getPdf(filename).subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          this.toastr.success('Report downloaded successfully');
        },
        error: (error) => {
          this.toastr.error('Failed to download report');
        }
      });
    }
  }

  onDownloadClick() {
    if (this.filterForm.valid) {
      let payload = {
        from_date: this.filterForm.get('from_date')?.value,
        to_date: this.filterForm.get('to_date')?.value,
      };
      this.portWeather.downloadReport(payload).subscribe({
        next: (data) => {
          if (data && data.content) {
            const { content, filename } = data;
            const byteCharacters = atob(content);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/json' });
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'reports_export.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          }
        },
        error: (error) => {
          this.toastr.error(ToasterMessages.Error);
        }
      });
    } else {
      this.toastr.info(ToasterMessages.SelectDate);
    }
  }

  isDarkMode() {
    return this.requestFormService.isDarkMode();
  }
}